import React, { useEffect, useState } from "react";
import { Button, Modal, Row, Col, Form, Upload, Progress, Spin, message, Input, Select } from 'antd';
import "./upload_assets_with_bar_modal.scss";
import { InboxOutlined, CheckCircleFilled, PlusOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from "common/hooks/redux";
import { toggleUploadAssetsWithBarModal } from "store/global";
import { RootState } from "store/index";
import lighthouse from '@lighthouse-web3/sdk';
import "../modals.scss";
import { getAssetTypesMini } from "services/assetType.service";
import { uploadMyFiles } from "services/myFiles.service";
import { LIGHTHOUSE_API_KEY, LIGHTHOUSE_API_URL } from "common/constants";
import { formatFileSize, validateFile } from "utils";

/**
 * @param {UploadType} props
 */

type FieldType = {
  name?: string;
  assetTypeId?: number;
  url?: any;
  mimetype?: any;
  size?: number;
  file?: any;
  hash: any;
  thumbnail: any
};

const UploadAssetsWithBarModal: React.FC = (
): React.ReactElement => {
  const dispatch = useAppDispatch();
  const visible = useAppSelector((state: RootState) => state.global.uploadAssetsWithBarModal);
  const { assetTypesMini } = useAppSelector(
    (state: RootState) => state.assetTypes
  );

  const { saveMyFilesLoading } = useAppSelector(
    (state: RootState) => state.myFiles
  );


  const [form] = Form.useForm();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<number>(0);
  const [file, setFile] = useState<File | null>(null);
  const [fileList, setFileList] = useState<any[]>([]);
  const [thumbUploadProgress, setThumbUploadProgress] = useState(0);
  const [uploadingThumb, setUploadingThumb] = useState(false);


  const toggleModal = (visible = false) => {
    dispatch(toggleUploadAssetsWithBarModal(visible));
  };

  useEffect(() => {
    dispatch(getAssetTypesMini());
  }, []);

  const resetModal = () => {
    const shouldClose =
      !uploading ||
      window.confirm(
        "Upload is in progress. If you cancel, the upload will be stopped. Do you want to continue?"
      );

    if (shouldClose) {
      form.resetFields();
      setFile(null);
      setFileList([]);
      setProgress(0);
      setUploading(false);
      toggleModal(false);

      if (uploading) {
        window.location.reload();
      }
    }
  };

  const handleUploadProgress = (data: any) => {
    const fraction = data?.progress;
    if (typeof fraction === "number") {
      const percent = Math.round(fraction * 100);
      if (percent < 99) {
        setProgress(percent);
      } else {
        setProgress(99);
      }
    }
  };

  const handleUploadProgressThumb = (progressData: any) => {
    const percentage = Math.round((progressData.total / progressData.uploaded) * 100);
    if (percentage < 99) {
      setThumbUploadProgress(percentage);
    } else {
      setThumbUploadProgress(99);
    }
  };

  const handleSubmit = async (values: FieldType) => {
    if (!file) {
      message.warning("Please select a file before submitting.");
      return;
    }

    try {
      // Validate main file
      const isValid = await validateFile(file);
      if (!isValid) return;

      // Determine if thumbnail is required:
      // - Non-image files
      // - OR images larger than 5MB
      const needsThumbnail = !file.type.startsWith("image/") || file.size / 1024 / 1024 > 5;

      // Validate thumbnail if required
      if (needsThumbnail && fileList.length > 0) {
        const isValidThumbnail = await validateFile(fileList[0].originFileObj);
        if (!isValidThumbnail) return;
      }

      setUploading(true);
      setProgress(0);

      const response = await lighthouse.upload(
        [file],
        LIGHTHOUSE_API_KEY,
        undefined,
        handleUploadProgress
      );

      let thumbUploadRes;
      if (needsThumbnail && fileList.length > 0) {
        setUploadingThumb(true);
        thumbUploadRes = await lighthouse.upload(
          [fileList[0].originFileObj],
          LIGHTHOUSE_API_KEY,
          undefined,
          handleUploadProgressThumb
        );
      }

      const url = `${LIGHTHOUSE_API_URL}/${response?.data.Hash}`;
      const thumbnail = thumbUploadRes ? `${LIGHTHOUSE_API_URL}/${thumbUploadRes?.data?.Hash}` : '';

      values.url = url;
      values.thumbnail = thumbnail ?? '';
      values.hash = response.data.Hash;
      values.mimetype = file.type;
      values.size = file.size;

      // Save metadata to DB
      await dispatch(uploadMyFiles(values)).unwrap();

      setProgress(100);
      message.success("File uploaded and saved successfully!");
      setFile(null);
      setFileList([]);
      form.resetFields();
      toggleModal(false);

    } catch (err) {
      console.error("Upload failed:", err);
      message.error("Upload failed.");
    } finally {
      setUploading(false);
      setUploadingThumb(false);
    }
  };

  const uploadProps = {
    multiple: false,
    maxCount: 1,
    showUploadList: true,
    beforeUpload: (selectedFile: File) => {
      setFile(selectedFile);
      setProgress(0);
      return false;
    },
    onRemove: () => {
      setFile(null);
      setProgress(0);
    },
    accept: [
      // Images
      "image/*",
      ".avif",
      ".svg",
      "image/svg+xml",

      // Video + audio
      "video/*",
      "audio/*",
      ".mov",
      "video/quicktime",

      // Documents
      ".pdf",
      ".doc",
      ".docx",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ".xls",
      ".xlsx",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ".ppt",
      ".pptx",
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      ".txt",

      // eBooks
      ".epub", // ✅ allow EPUB explicitly
      "application/epub+zip",
    ].join(","),
  };

  const getStatusMessage = () => {
    if (progress < 20) return 'Initializing upload...';
    if (progress < 60) return 'Uploading...';
    if (progress < 90) return 'Processing...';
    if (progress < 100) return 'Almost done...';
    return 'Upload complete!';
  };

  const sortedTypes = [...assetTypesMini].sort((a: any, b: any) => {
    const nameA = a.name.toLowerCase();
    const nameB = b.name.toLowerCase();
    if (nameA < nameB) return -1;
    if (nameA > nameB) return 1;
    return 0;
  });

  const handleChange = ({ fileList }: any) => {
    setFileList(fileList);

    // Extract the file object from fileList (if it exists)
    const file = fileList.length > 0 ? fileList[0].originFileObj : null;

    // Set form field value properly
    form.setFieldsValue({ thumbnail: file });

    // Trigger validation manually
    form.validateFields(["thumbnail"]);
  };

  return (
    <Modal
      open={visible}
      title="Choose an asset"
      width="auto"
      style={{ maxWidth: '700px' }}
      wrapClassName="upload-assets-with-bar"
      onCancel={resetModal}
      footer={[
        <Button onClick={resetModal} key="cancel" className="footer_btn_left">Cancel</Button>,
        <Button
          type="primary"
          htmlType="submit"
          loading={uploading}
          className="footer_btn"
          onClick={() => form.submit()}
          key="submit"
        >
          Submit
        </Button>
      ]}
    >
      <Form
        form={form}
        name="upload-asset"
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
        className="upload-assets"
      >
        <Row gutter={24}>
          <Col className="gutter-row" span={24}>
            <Form.Item<FieldType>
              label="Name"
              name="name"
              rules={[
                { required: true, message: "Please enter name" },
                { whitespace: true, message: "Please enter valid data." },
              ]}
            >
              <Input placeholder="Please enter name" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col className="gutter-row" span={24}>
            <Form.Item<FieldType>
              label="Choose asset type"
              name="assetTypeId"
              rules={[{ required: true, message: "Please choose type" }]}
            >
              <Select
                placeholder="asset type"
                options={sortedTypes?.map((item: { name: any; id: any }) => ({
                  label: item.name,
                  value: item.id,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            {!uploading && (
              <Form.Item<FieldType>
                label="Choose asset"
                name="url"
                rules={[
                  { required: true, message: "Please choose Asset" },
                  {
                    validator: (_, value) => {
                      if (value?.file?.type === "image/tiff") {
                        return Promise.reject(new Error("Unsupported file format!"));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Upload.Dragger {...uploadProps} className="drag-upload">
                  <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                  <p className="ant-upload-text">Browse or drag files here to upload</p>
                  <p className="ant-upload-hint">Support for a single upload.</p>
                  {file && (
                    <p>
                      {file.name}{" "}
                      <span style={{ color: "#999" }}>
                        ({formatFileSize(file.size)})
                      </span>
                    </p>
                  )}
                </Upload.Dragger>
              </Form.Item>
            )}

            {file && uploading && (
              <div className="upload-preview" style={{ textAlign: "center", marginTop: 20 }}>
                <h4>{file.name}{" "}</h4>
                <p style={{ color: "#999", fontWeight: "normal" }}>
                  ({formatFileSize(file.size)})
                </p>

                {progress === 100 ? (
                  <CheckCircleFilled style={{ fontSize: 54, color: '#C1FC49', marginBottom: 10 }} />
                ) : (
                  <Spin size="large" style={{ margin: "10px 0" }} spinning={uploading} />
                )}

                <div style={{ marginTop: 20 }}>
                  <Progress percent={progress} status="active" strokeColor="#C1FC49" />

                  <p style={{ marginTop: 5 }}>
                    {getStatusMessage()}
                  </p>
                </div>
              </div>
            )}
          </Col>
        </Row>
        {file?.type && (!file.type.startsWith("image/") || file?.size / 1024 / 1024 > 5) && (
          <Form.Item
            label="Upload thumbnail"
            name="thumbnail"
            rules={[
              {
                validator: (_, value) => {
                  if (!value) {
                    return Promise.reject(new Error("Please select a thumbnail"));
                  }
                  if (value.size / 1024 / 1024 > 1) {
                    return Promise.reject(new Error("File must be smaller than 1MB!"));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Upload
              name="avatar"
              listType="picture-card"
              className="avatar-uploader thumbnail-uploader"
              showUploadList={{ showPreviewIcon: false, showRemoveIcon: true }}
              maxCount={1}
              fileList={fileList}
              onChange={handleChange}
              // accept="all"
              beforeUpload={(file) => {
                if (file.size / 1024 / 1024 > 1) {
                  message.error("File must be smaller than 1MB!");
                  return Upload.LIST_IGNORE; // Prevents upload
                }
                return false; // Prevents auto-upload but keeps file in `fileList`
              }}
            >
              {fileList.length === 0 && (
                <button style={{ border: 0, background: "none" }} type="button">
                  <PlusOutlined style={{ color: "#c1fc49" }} />
                  <div style={{ marginTop: 8, color: "#c1fc49" }}>Upload</div>
                </button>
              )}
            </Upload>
          </Form.Item>


        )}
      </Form>
    </Modal>
  );
};

export default UploadAssetsWithBarModal;